/**
 * useEndOfLiveQuiz — le quiz de fin de séance, joué : l'offre de Sam, les questions une à une, le
 * résultat.
 *
 * Le jeu est celui du défi éclair (`challengeReducer`) : juste ou faux tout de suite, la série des
 * bonnes réponses d'affilée, sans « Rejouer ». Chaque réponse part au serveur sans que le jeu
 * l'attende ; une seule qui n'arrive pas, et le pétale n'est pas gagné (le serveur ne le donne qu'à
 * la série répondue jusqu'au bout). La dernière arrivée, le compte est relu : la coquille montre le
 * pétale gagné, comme après le quiz de prérequis. Rouvert dans la même sortie (une page rechargée),
 * le quiz reprend après les réponses déjà données, que le serveur refuserait une seconde fois — sauf
 * si l'élève a relu le live entre-temps (resté une minute sur le message de clôture) : le serveur y
 * voit un retour dans la salle, et tire une série neuve.
 */
import { useCallback, useEffect, useMemo, useReducer, useState } from "react"
import { useDispatch } from "react-redux"

import { authService } from "../../../data/services/authService"
import {
    challengeReducer,
    NEW_GAME,
    playableQuestions,
    type ApiChallengeQuestion,
    type ChallengeGame,
} from "../../activity/correction/correctionChallenge"
import { apiCall } from "../../shared/apiCall"
import { type EngineTelemetry } from "../../shared/telemetry"
import { answerEndOfLiveQuiz, endOfLiveQuizOf, type ApiEndOfLiveQuiz } from "./endOfLiveQuiz"

export type EndOfLiveQuizPhase = "offer" | "question" | "result"

export type EndOfLiveQuizController = {
    /** `undefined` tant qu'il se demande ; `null` : rien à jouer, la suite vient. */
    quiz: ApiEndOfLiveQuiz | null | undefined
    /** La série jouable, dans son ordre. */
    questions: ApiChallengeQuestion[]
    /** Le rang de la première question jouée ici : après celles déjà répondues. */
    from: number
    phase: EndOfLiveQuizPhase
    /** Le jeu, ses rangs comptés à partir de `from`. */
    game: ChallengeGame
    /** La réponse touchée, par rang de la série. */
    picks: Record<number, number>
    /** Le pétale est gagné : la série finie, chaque réponse arrivée, et pas déjà gagné sur ce live. */
    earned: boolean
    /**
     * Aucune réponse donnée ici n'a été refusée ni perdue jusqu'ici — au-delà de la demi-heure qui
     * suit la première réponse, le serveur les refuse : elles ne comptent pas, une ratée ne reviendra
     * pas. Une réponse encore en route compte comme arrivée : refusée ensuite, le résultat se corrige.
     */
    saved: boolean
    start: () => void
    answer: (index: number) => void
    /** « Question suivante » ; à la dernière, « Voir mon score ». */
    next: () => void
}

export function useEndOfLiveQuiz(liveSessionId: number | null, telemetry: EngineTelemetry): EndOfLiveQuizController {
    const dispatch = useDispatch()
    const [served, setServed] = useState<ApiEndOfLiveQuiz | null | undefined>(undefined)
    const [phase, setPhase] = useState<EndOfLiveQuizPhase>("offer")
    const [game, play] = useReducer(challengeReducer, NEW_GAME)
    const [picks, setPicks] = useState<Record<number, number>>({})
    const [lost, setLost] = useState(false)

    useEffect(() => {
        if (liveSessionId == null) {
            setServed(null)
            return
        }
        let current = true
        void endOfLiveQuizOf(dispatch as never, liveSessionId, telemetry).then((quiz) => {
            if (current) setServed(quiz)
        })
        return () => {
            current = false
        }
        // Une fois, à l'arrivée sur l'écran.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveSessionId])

    const questions = useMemo(() => playableQuestions(served?.questions), [served])
    const from = Math.min(Math.max(served?.answered ?? 0, 0), questions.length)
    // Plus rien à répondre : pas de quiz.
    const quiz = served === undefined ? undefined : served != null && from < questions.length ? served : null
    const rank = from + game.index
    const question = questions[rank]

    const answer = useCallback(
        (index: number) => {
            if (quiz == null || question == null || game.selected !== null) return
            setPicks((current) => ({ ...current, [rank]: index }))
            play({ type: "ANSWER", index, correctIndex: question.correct_index as number })
            const last = rank === questions.length - 1
            answerEndOfLiveQuiz(quiz.id, question, index).then(
                () => {
                    if (!last || (quiz.petals ?? 0) <= 0) return
                    apiCall(dispatch as never, authService.getUser as never).catch((cause: unknown) =>
                        telemetry.error("refresh_user", cause)
                    )
                },
                (cause: unknown) => {
                    setLost(true)
                    telemetry.error("answer", cause)
                }
            )
        },
        [quiz, question, game.selected, rank, questions.length, dispatch, telemetry]
    )

    const next = useCallback(() => {
        if (game.selected === null) return
        if (rank >= questions.length - 1) setPhase("result")
        else play({ type: "NEXT" })
    }, [game.selected, rank, questions.length])

    return {
        quiz,
        questions,
        from,
        phase,
        game,
        picks,
        earned: phase === "result" && (quiz?.petals ?? 0) > 0 && !lost,
        saved: !lost,
        start: () => setPhase("question"),
        answer,
        next,
    }
}
